import { Component, OnInit } from '@angular/core';
import { OrdersService } from './orders.service';
import { PurchaseOrder } from './orders.model';
import { Router } from '@angular/router';
import { ContactModel } from '../../contact/contact/contact.model';
import { ConceptModel } from '../concept/concept.model';
import { ContactService } from '../../contact/contact/contact.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ConfirmReceptionDialogComponent } from './confirm-reception-dialog.component';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-orders-list',
    templateUrl: './orders-list.component.html',
    styleUrls: ['./orders-list.component.css'],
    standalone: false
})
export class OrdersListComponent implements OnInit {
    orders: PurchaseOrder[] = [];
    providers: ContactModel[] = [];
    concepts: ConceptModel[] = [];
    ready = false;

    orderRows: PurchaseOrder[] = [];
    displayedColumns: string[] = ['orderDate', 'orderNumber', 'supplierName', 'status', 'actions'];

    hideConfirmed = false;
    supplierFilter = '';
    dateFilter: Date = null;

    loading = false;

    constructor(
        private ordersService: OrdersService,
        private contactService: ContactService,
        private router: Router,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit() {
        this.loadOrders();
    }

    loadOrders() {
        this.ready = false;

        this.ordersService.getOrders().subscribe(data => {
            if (data && data['data'] && Array.isArray(data['data'])) {
                this.orders = data['data'];
            } else {
                this.orders = [];
            }

            this.contactService.getList().then(contacts => {
                this.providers = contacts.list;

                this.orders.forEach(order => {
                    const provider = this.providers.find(p => p.uid === order.supplierUid);

                    if (provider) {
                        order.supplierName = provider.companyName;
                    } else {
                        order.supplierName = '';
                    }
                });

                this.applyFilters();
                this.ready = true;
            }).catch(() => {
                this.providers = [];
                this.applyFilters();
                this.ready = true;
            });
        }, () => {
            this.orders = [];
            this.orderRows = [];
            this.ready = true;
        });
    }

    applyFilters() {
        let rows = this.orders.slice();

        if (this.hideConfirmed) {
            rows = rows.filter(x => x.status === 0);
        }

        if (this.supplierFilter && this.supplierFilter.trim() !== '') {
            const text = this.supplierFilter.trim().toLowerCase();

            rows = rows.filter(x => {
                if (!x.supplierName) {
                    return false;
                }

                return x.supplierName.toLowerCase().indexOf(text) !== -1;
            });
        }

        if (this.dateFilter) {
            const selected = new Date(this.dateFilter);

            rows = rows.filter(x => {
                if (!x.orderDate) {
                    return false;
                }

                const orderDate = new Date(x.orderDate);

                return orderDate.getFullYear() === selected.getFullYear()
                    && orderDate.getMonth() === selected.getMonth()
                    && orderDate.getDate() === selected.getDate();
            });
        }

        rows.sort((a, b) => {
            if (a.status !== b.status) {
                return a.status - b.status;
            }

            const dateA = new Date(a.orderDate).getTime();
            const dateB = new Date(b.orderDate).getTime();

            return dateB - dateA;
        });

        this.orderRows = rows;
    }

    clearFilters() {
        this.hideConfirmed = false;
        this.supplierFilter = '';
        this.dateFilter = null;
        this.applyFilters();
    }

    goCreate() {
        this.router.navigate(['/concept/orders/create']);
    }

    goEdit(uid: string, view: boolean) {
        if (view) {
            this.router.navigate(['/concept/orders/view/' + uid]);
        } else {
            this.router.navigate(['/concept/orders/edit/' + uid]);
        }
    }

    confirmReception(order: PurchaseOrder): void {
        this.ordersService.getOrder(order.uid).subscribe(fullOrder => {
            this.dialog.open(ConfirmReceptionDialogComponent, {
                width: '800px',
                maxWidth: '95vw',
                data: fullOrder
            }).afterClosed().subscribe(result => {
                if (result) {
                    this.loadOrders();
                }
            });
        });
    }

    deleteOrder(order: PurchaseOrder): void {
        this.ordersService.deleteOrder(order.id).subscribe(() => {
            this.displayInfo('Pedido eliminado con éxito');
            this.loadOrders();
        });
    }

    public displayInfo(message: string): void {
        const options = new MatSnackBarConfig();
        options.duration = 10000;
        options.panelClass = ['custom-snackbar', 'custom-snackbar-container'];
        this.snackBar.open(message, '', options);
    }

    downloadPdf(order: PurchaseOrder) {
        this.loading = true;
        this.ordersService.downloadPdf(order.id)
            .subscribe(blob => {
                const file = new Blob([blob], { type: 'application/pdf' });
                FileSaver.saveAs(file, 'order-' + order.id + '.pdf');
                this.loading = false;
            });
    }

    downloadExcel(order: PurchaseOrder) {
        this.loading = true;
        this.ordersService.downloadExcel(order.id)
            .subscribe(blob => {
                const file = new Blob([blob], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                FileSaver.saveAs(file, 'order-' + order.id + '.xlsx');
                this.loading = false;
            });
    }

    duplicateOrder(order: PurchaseOrder): void {
        this.ordersService.duplicateOrder(order.uid).subscribe(() => {
            this.displayInfo('Pedido duplicado con éxito');
            this.loadOrders();
        });
    }
}